import React, { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Skeleton, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import cx from 'classnames'
import _ from 'lodash'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ReactComponent as ArrowIcon } from 'assets/arrow_right.svg'
import { StoreStatus } from 'common/utils/types'
import theme from 'common/utils/theme'

import { type QuestionAnswer, type Question } from '../questionnaires.types'
import {
  getQuestionnaire,
  getQuestionnaireResults,
  postQuestionnaireResponse,
} from '../questionnaires.service'
import AiQuestion from '../ai-question'
import { initializeVendor } from '../../vendor.service'

import styles from './AIQuestionnaire.module.scss'

const AIQuestionnaire: React.FC = (): ReactElement => {
  const { palette } = useTheme()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const largeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const { questionnaire, questionnaireStatus } = useAppSelector((state) => state.questionnaires)
  const { vendor } = useAppSelector((state) => state.vendor)

  const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0)
  const [answers, setAnswers] = useState<Record<string, QuestionAnswer[]>>({})
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (questionnaire) {
      if (questionnaire?.id && !(questionnaire?.result && questionnaire?.result?.completedAt)) {
        void dispatch(getQuestionnaire())
      }
    }
  }, [])

  const groups = useMemo<Array<{ id: string; name: string }>>(() => {
    return _.uniqBy(questionnaire?.questions?.map((q) => q.group) ?? [], (g) => g.id)
  }, [questionnaire])

  const currentQuestions = useMemo<Question[]>(() => {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 0)
    const currentGroupId = groups[currentGroupIndex]?.id
    return questionnaire?.questions?.filter((q) => q.group.id === currentGroupId) ?? []
  }, [currentGroupIndex, questionnaire])

  const nextDisabled = useMemo<boolean>(() => {
    return !currentQuestions.every((q) => answers[q.id]?.length)
  }, [currentQuestions, answers])

  useEffect(() => {
    setErrorMessage('')
  }, [questionnaire, answers, currentGroupIndex])

  const handleGroupSelect = (groupIndex: number): void => {
    if (groupIndex === 0) {
      setCurrentGroupIndex(groupIndex)
      return
    }
    const currentGroupId = groups[groupIndex - 1]?.id
    const groupQuestions = questionnaire?.questions?.filter((q) => q.group.id === currentGroupId) ?? []
    if (!groupQuestions.every((q) => answers[q.id]?.length)) {
      setErrorMessage('Morate odgovoriti na sva prethodna pitanja')
    } else {
      setCurrentGroupIndex(groupIndex)
    }
  }

  const startTime = useMemo<string>(() => {
    return questionnaire ? new Date().toISOString() : ''
  }, [questionnaire])

  const handleButtonClick = (): void => {
    if (questionnaire?.questions && currentGroupIndex !== groups.length - 1) {
      setCurrentGroupIndex((prev) => prev + 1)
    } else {
      void dispatch(
        postQuestionnaireResponse({
          companyId: vendor?.id ?? '',
          questionnaireId: questionnaire?.id ?? '',
          questionnaireVersionId: questionnaire?.versionId ?? '',
          response: {
            startTime,
            answers:
              questionnaire?.questions?.map((q) => {
                const questionAnswers = answers[q.id]
                return {
                  questionId: q.id,
                  answerIds: questionAnswers?.map((qa) => qa.id) ?? [],
                }
              }) ?? [],
          },
        }),
      ).then(() => {
        void dispatch(getQuestionnaire())
        void dispatch(initializeVendor())
        if (vendor && questionnaire) {
          void dispatch(getQuestionnaireResults({ companyId: vendor.id, questionnaireId: questionnaire.id }))
        }
        navigate('rezultati', { replace: true })
      })
    }
  }

  const handleBackClick = (): void => {
    if (currentGroupIndex !== 0) {
      setCurrentGroupIndex((prev) => prev - 1)
    }
  }

  const handleSelectAnswer = (answer: QuestionAnswer, questionId: string): void => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: [answer],
    }))
  }

  const progress = useMemo<number>(() => {
    return (100 * Object.keys(answers).length) / (questionnaire?.questions?.length ?? 1)
  }, [answers])

  return (
    <div className={styles.container}>
      {questionnaireStatus === StoreStatus.Loading ? (
        <div className={styles.loader} style={{ flexDirection: largeScreen ? 'row' : 'column' }}>
          <Skeleton
            height={350}
            width={largeScreen ? '600px' : '100%'}
            variant="rectangular"
            sx={{ backgroundColor: palette.blue800.main }}
          />
          <Skeleton
            height={largeScreen ? 350 : 50}
            variant="rectangular"
            sx={{ backgroundColor: palette.blue800.main, flexGrow: 1 }}
          />
        </div>
      ) : (
        questionnaire?.questions && (
          <div className={styles.questionnaireContainer}>
            <div className={styles.questionImageContainer}>
              <div className={styles.questionContainer}>
                <div className={styles.groupProgress}>
                  {groups.map((group, index) => (
                    <div
                      key={group.id}
                      className={cx(styles.groupProgressItem, {
                        [styles.selected]: index === currentGroupIndex,
                      })}
                    >
                      <Typography color={palette.gray.main} variant="title3" className={styles.groupName}>
                        {group.name}
                      </Typography>
                      <div
                        className={styles.groupLinearBar}
                        onClick={() => {
                          handleGroupSelect(index)
                        }}
                      />
                    </div>
                  ))}
                </div>
                <Typography variant="title3">{progress.toFixed(0)}% završeno</Typography>
                {errorMessage && (
                  <Typography variant="title3" color={palette.charts.red.main}>
                    {errorMessage}
                  </Typography>
                )}
                {currentQuestions.map((question) => (
                  <div className={styles.singleQuestion} key={question.id}>
                    <AiQuestion
                      question={question}
                      currentAnswerIds={answers[question.id]?.map((a) => a.id) ?? []}
                      handleSelectAnswer={(answer) => {
                        handleSelectAnswer(answer, question.id)
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.footer}>
              {currentGroupIndex !== 0 && (
                <div className={cx(styles.letter, styles.backButton)} onClick={handleBackClick}>
                  <ArrowIcon className={styles.icon} fill="#afb9c5" />
                </div>
              )}
              <button
                className={cx(styles.button, { [styles.disabled]: nextDisabled })}
                onClick={handleButtonClick}
                disabled={nextDisabled}
              >
                {currentGroupIndex === groups.length - 1 ? 'Završi upitnik' : 'Sledeća sekcija'}
              </button>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default AIQuestionnaire
